class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', (event) => {
      if (event.target.matches('[data-facet-search]')) {
        FacetFiltersForm.searchValues.set(event.target.dataset.facetSearch, event.target.value);
        FacetFiltersForm.filterOptions(event.target);
        return;
      }
      // A newer interaction invalidates an older response immediately.
      FacetFiltersForm.requestVersion += 1;
      this.debouncedOnSubmit(event);
    });
    facetForm.addEventListener('submit', (event) => {
      event.preventDefault();
      if (!document.activeElement?.matches('[data-facet-search]')) this.onSubmitHandler(event);
    });

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static filterOptions(input) {
    const details = input.closest('.js-filter');
    if (!details) return;
    const query = input.value.trim().toLocaleLowerCase();
    let visible = 0;
    details.querySelectorAll('li').forEach((item) => {
      const matches = item.textContent.toLocaleLowerCase().includes(query);
      item.hidden = !matches;
      if (matches) visible += 1;
    });
    const empty = details.querySelector('.spoilt-facet-empty');
    if (empty) empty.hidden = visible > 0;
  }

  static restoreSearch() {
    document.querySelectorAll('[data-facet-search]').forEach((input) => {
      input.value = FacetFiltersForm.searchValues.get(input.dataset.facetSearch) || '';
      FacetFiltersForm.filterOptions(input);
    });
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    if (window.SpoiltSaleFilters) return window.SpoiltSaleFilters.render(searchParams, event, updateURLHash);
    if (window.SpoiltSubstringSearch) return window.SpoiltSubstringSearch.render(searchParams, event, updateURLHash);
    FacetFiltersForm.requestVersion += 1;
    document.querySelector('.spoilt-filter-error')?.remove();
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const updateEvent = FacetFiltersForm.startUpdateEvent(searchParams);
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, updateEvent)
        : FacetFiltersForm.renderSectionFromFetch(url, event, updateEvent);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static startUpdateEvent(searchParams) {
    const { SearchUpdateEvent, CollectionUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent && !CollectionUpdateEvent) return;

    const facetsForm = document.querySelector('facet-filters-form');
    const facetsContainer = document.querySelector('.facets-container');
    const collectionId = facetsContainer?.dataset.collectionId || null;
    const collectionHandle = facetsContainer?.dataset.collectionHandle || '';
    const currentCount = parseInt(document.getElementById('ProductCount')?.dataset.productCount) || 0;
    const urlSearchParams = new URLSearchParams(searchParams);
    const isSearchPage = facetsContainer?.dataset.template === 'search';
    const isCollectionPage = facetsContainer?.dataset.template === 'collection';
    const dispatchTarget = facetsForm || document;

    let deferred;

    if (isSearchPage) {
      deferred = SearchUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new SearchUpdateEvent({
          search: {
            query: urlSearchParams.get('q') || '',
            productFilters: SearchUpdateEvent.parseProductFilters(urlSearchParams),
            sortKey: SearchUpdateEvent.getSortKey(urlSearchParams),
          },
          promise: deferred.promise,
        }),
      );
    } else if (isCollectionPage) {
      // collectionId is null for auto-generated collections (/collections/all,
      // /collections/vendors/<x>, etc.) — the SSE schema accepts that.
      deferred = CollectionUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new CollectionUpdateEvent({
          collection: { id: collectionId, handle: collectionHandle, productsCount: currentCount },
          productFilters: CollectionUpdateEvent.parseProductFilters(urlSearchParams),
          sortKey: CollectionUpdateEvent.getSortKey(urlSearchParams),
          promise: deferred.promise,
        }),
      );
    }

    if (!deferred) return;

    return {
      resolve: (filteredCount) => {
        deferred.resolve({ [isSearchPage ? 'totalCount' : 'productsCount']: filteredCount });
      },
      reject: deferred.reject,
    };
  }

  static renderSectionFromFetch(url, event, updateEvent) {
    const version = FacetFiltersForm.requestVersion;
    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Filter request failed');
        return response.text();
      })
      .then((html) => {
        if (version !== FacetFiltersForm.requestVersion) { updateEvent?.resolve(0); return; }
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData.slice(-19), { html, url }];
        FacetFiltersForm.renderSection(html, event, updateEvent);
      })
      .catch((error) => {
        if (version !== FacetFiltersForm.requestVersion) { updateEvent?.resolve(0); return; }
        document.querySelectorAll('#ProductGridContainer .loading, .product-count.loading, .product-count-vertical.loading').forEach(el => el.classList.remove('loading'));
        document.querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner').forEach(el => el.classList.add('hidden'));
        FacetFiltersForm.toggleActiveFacets(false);
        const status = document.createElement('p');
        status.className = 'spoilt-filter-error';
        status.setAttribute('role', 'alert');
        status.textContent = 'Could not update products. Please try your filter again.';
        document.querySelector('.facets-container')?.append(status);
        updateEvent?.reject(error);
      });
  }

  static renderSectionFromCache(filterDataUrl, event, updateEvent) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderSection(html, event, updateEvent);
  }

  static renderSection(html, event, updateEvent) {
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.restoreSearch();
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html, updateEvent);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html, updateEvent) {
    const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
    const sourceCount = parsedHtml.getElementById('ProductCount');
    const count = sourceCount.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.dataset.productCount = sourceCount.dataset.productCount || '';
    container.dataset.totalCount = sourceCount.dataset.totalCount || '';
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));

    updateEvent?.resolve(parseInt(sourceCount.dataset.productCount) || 0);
  }

  static renderFilters(html, event) {
    document.querySelectorAll('[data-facet-search]').forEach(input => FacetFiltersForm.searchValues.set(input.dataset.facetSearch, input.value));
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (!isTextInput) {
          // Try to return focus to the same checkbox the user just toggled,
          // re-selecting it from the freshly rendered HTML by its id.
          const originatingInputId = event.target.id;
          const matchingInput = originatingInputId
            ? newFacetDetailsElement.querySelector(`#${CSS.escape(originatingInputId)}`)
            : null;

          if (matchingInput) {
            matchingInput.focus();
          } else {
            // Fallback to summary/close button if the checkbox can't be found
            const fallbackSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
              ? `.mobile-facets__close-button`
              : `.facets__summary`;
            const fallbackElement = newFacetDetailsElement.querySelector(fallbackSelector);
            if (fallbackElement) fallbackElement.focus();
          }
        }
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    // The main summary and delegated form handlers survive these updates.
    // Rebinding here stacks click handlers and makes the drawer toggle twice.
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const targetForm = event.target.closest('form');
    const drawer = document.querySelector('.facets-container-drawer');
    const mobile = drawer || targetForm?.id === 'FacetFiltersFormMobile';
    const filterForm = document.getElementById(mobile ? 'FacetFiltersFormMobile' : 'FacetFiltersForm');
    const form = filterForm || targetForm;
    if (!form) return;
    form.querySelectorAll('price-range').forEach(range => {
      range.textInputs.forEach(input => range.adjustToValidValues(input));
      range.setMinAndMaxValues();
      range.syncSlidersFromInputs();
    });
    const params = new URLSearchParams(new FormData(form));
    const currentParams = new URLSearchParams(window.location.search);
    if (currentParams.has('type')) params.set('type', currentParams.get('type'));
    const sort = event.target.name === 'sort_by' ? event.target :
      (form.querySelector('select[name="sort_by"]') || document.querySelector('#SortBy'));
    if (sort) params.set('sort_by', sort.value);
    this.onSubmitForm(params.toString(), event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchValues = new Map();
FacetFiltersForm.requestVersion = 0;
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    // Only the plain min/max text fields - excludes the visual range
    // slider inputs added below, which don't have a name attribute and
    // must not be treated as the same kind of field (they don't carry
    // the data-min/data-max attributes this validation relies on).
    this.textInputs = Array.from(this.querySelectorAll('.field__input'));
    this.textInputs.forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();

    this.sliderMin = this.querySelector('.price-range-slider__input--min');
    this.sliderMax = this.querySelector('.price-range-slider__input--max');
    this.sliderTrackActive = this.querySelector('.price-range-slider__track-active');

    if (this.sliderMin && this.sliderMax) {
      this.sliderMin.addEventListener('input', this.onSliderInput.bind(this));
      this.sliderMax.addEventListener('input', this.onSliderInput.bind(this));
      this.updateSliderTrack();
    }
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
    this.syncSlidersFromInputs();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const minInput = this.textInputs[0];
    const maxInput = this.textInputs[1];
    if (!minInput || !maxInput) return;
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    if (input.value.trim() === '') return;
    const value = Math.round(Number(input.value.replace(/[, ]/g, '')) / 500) * 500;
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    input.value = String(Math.min(max, Math.max(min, Number.isFinite(value) ? value : min)));
  }

  // Dragging either slider handle updates the matching text field (which
  // triggers the existing debounced filter submit) and keeps the two
  // handles from crossing each other.
  onSliderInput(event) {
    const movedMin = event.currentTarget === this.sliderMin;
    let minVal = parseFloat(this.sliderMin.value);
    let maxVal = parseFloat(this.sliderMax.value);

    if (minVal > maxVal) {
      if (movedMin) {
        minVal = maxVal;
        this.sliderMin.value = String(minVal);
      } else {
        maxVal = minVal;
        this.sliderMax.value = String(maxVal);
      }
    }

    this.updateSliderTrack();

    const [textMin, textMax] = this.textInputs;
    const target = movedMin ? textMin : textMax;
    if (!target) return;

    target.value = this.formatSliderValue(movedMin ? minVal : maxVal);
    this.setMinAndMaxValues();
    // The form only listens for native 'input' events to trigger
    // re-filtering, so re-dispatch one from the text field the slider
    // is driving (bubbles up to the <form>'s listener).
    target.dispatchEvent(new Event('input', { bubbles: true }));
  }

  // Keep the slider handles in sync when the user types a value directly
  // into the min/max text fields instead of dragging.
  syncSlidersFromInputs() {
    if (!this.sliderMin || !this.sliderMax) return;
    const [textMin, textMax] = this.textInputs;
    this.sliderMin.value = textMin?.value ? textMin.value.replace(/,/g, '') : this.sliderMin.min;
    this.sliderMax.value = textMax?.value ? textMax.value.replace(/,/g, '') : this.sliderMax.max;
    this.updateSliderTrack();
  }

  updateSliderTrack() {
    if (!this.sliderTrackActive || !this.sliderMin) return;
    const max = parseFloat(this.sliderMin.max) || 0;
    const minVal = parseFloat(this.sliderMin.value) || 0;
    const maxVal = Number(this.sliderMax.value);
    const minPercent = max > 0 ? (minVal / max) * 100 : 0;
    const maxPercent = max > 0 ? (maxVal / max) * 100 : 100;
    this.sliderTrackActive.style.left = `${minPercent}%`;
    this.sliderTrackActive.style.width = `${Math.max(0, maxPercent - minPercent)}%`;
  }

  formatSliderValue(value) {
    return Number.isInteger(value) ? String(value) : value.toFixed(2);
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);

// Tapping the dimmed backdrop area of the mobile filter drawer (anywhere
// outside the sliding panel itself) now closes the drawer, instead of the
// tap doing nothing. Scoped to .mobile-facets specifically so this doesn't
// change behavior for any other menu-drawer (e.g. the header hamburger menu).
document.addEventListener('click', (event) => {
  const close = event.target.closest('[data-close-filters]');
  if (close) {
    event.preventDefault();
    event.stopImmediatePropagation();
    const drawer = close.closest('menu-drawer');
    drawer?.closeMenuDrawer(event, drawer.querySelector('summary'));
    return;
  }
  const backdrop = event.target.closest('.mobile-facets');
  if (!backdrop) return;
  if (event.target.closest('.mobile-facets__inner')) return;

  const drawer = backdrop.closest('menu-drawer');
  if (!drawer || typeof drawer.closeMenuDrawer !== 'function') return;

  drawer.closeMenuDrawer(event, drawer.querySelector('summary'));
}, true);

// Grade filter (1:1 / A+ / A, etc.): keep the name on a single line no
// matter how long it is, by shrinking its font size to fit instead of
// letting it wrap. CSS (see the matching rules in component-facets.css)
// sets these labels to white-space: nowrap so their natural unwrapped
// width is what gets measured here; this script does the actual
// measuring and shrinking, since that part can't be done in CSS alone.
(function () {
  var GRADE_LABEL_SELECTOR =
    ".facets__list--vertical[class*='facets__list--'][class*='grade'] .facet-checkbox__text-label, " +
    ".mobile-facets__list[class*='facets__list--'][class*='grade'] .facet-checkbox__text-label";
  var MIN_FONT_SIZE_PX = 10;
  var STEP_PX = 0.5;

  function fitGradeLabel(label) {
    // Reset to the CSS-defined size first so this is measured fresh each
    // time, rather than compounding shrinkage from a previous pass.
    label.style.fontSize = '';
    var fontSize = parseFloat(window.getComputedStyle(label).fontSize) || MIN_FONT_SIZE_PX;

    while (label.scrollWidth > label.clientWidth && fontSize > MIN_FONT_SIZE_PX) {
      fontSize -= STEP_PX;
      label.style.fontSize = fontSize + 'px';
    }
  }

  function fitAllGradeLabels() {
    document.querySelectorAll(GRADE_LABEL_SELECTOR).forEach(fitGradeLabel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fitAllGradeLabels);
  } else {
    fitAllGradeLabels();
  }

  // Web fonts can swap in after first paint and change measured text
  // width, so run once more once they're ready.
  if (window.document.fonts && window.document.fonts.ready) {
    window.document.fonts.ready.then(fitAllGradeLabels);
  }

  // Re-fit whenever the filter panel's markup changes - e.g. the AJAX
  // re-render FacetFiltersForm does on every filter/sort change, or the
  // mobile drawer opening. Scoped to .facets-container (rather than the
  // whole document) so this doesn't fire on unrelated page activity like
  // the cart drawer.
  var containers = document.querySelectorAll('.facets-container');
  if (containers.length && window.MutationObserver) {
    var observer = new MutationObserver(function () {
      fitAllGradeLabels();
    });
    containers.forEach(function (container) {
      observer.observe(container, { childList: true, subtree: true });
    });
  }

  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(fitAllGradeLabels, 150);
  });
})();

